import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  TextField,
  InputAdornment,
  TableContainer,
  Table,
  TableBody,
  Paper,
  Checkbox,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import UndoIcon from "@mui/icons-material/Undo";
import { InvoiceNumberTooltip, InvoiceSelectionTooltip } from "../../invoices/Tooltips";
import FastEntryInvoiceLineItem from "./FastEntryInvoiceLineItem";
import NewFastEntryLineItem from "./NewFastEntryLineItem";
import useLineItemManagement from "./hooks/useLineItemManagement";
import {
  selectInvoiceExistsInComposer,
  removeInvoiceItems,
} from "features/composerSlice";
import { useSelector, useDispatch } from "react-redux";

// Styled button component that follows the Delete button styling
const ActionButton = ({ icon, label, onClick, hoverColor = "#f44336" }) => {
  return (
    <Button
      startIcon={icon}
      onClick={onClick}
      sx={{
        // width: "fit-content",
        textTransform: "none",
        color: "#373737",
        fontSize: "0.75rem",
        minWidth: "auto",
        padding: "4px 8px",
        transition: "color 0.2s ease",
        "& .MuiSvgIcon-root": {
          transition: "color 0.2s ease",
          color: "#373737",
        },
        "&:hover": {
          backgroundColor: "rgba(0,0,0,0.04)",
          color: hoverColor,
          "& .MuiSvgIcon-root": {
            color: hoverColor,
          },
        },
      }}
    >
      {label}
    </Button>
  );
};

// Add memoized version of FastEntryInvoiceLineItem
const MemoizedFastEntryInvoiceLineItem = React.memo(FastEntryInvoiceLineItem);

const FastEntryInvoice = ({ invoice, onDelete, onUpdate, selected, onToggleSelected }) => {
  const [invoiceNumber, setInvoiceNumber] = useState(
    invoice.invoiceNumber || ""
  );
  const [isTextFieldFocused, setIsTextFieldFocused] = useState(false);
  const invoiceExistsInComposer = useSelector((state) =>
    selectInvoiceExistsInComposer(state, invoice.id.toString())
  );
  const dispatch = useDispatch();
  const debounceTimeout = useRef();
  // Use our custom hook for line item management
  const {
    lineItems,
    newestItemId,
    duplicatedItemId,
    hotKeyCreatedItemId,
    resetPoppers,
    handleAddNewLineItem,
    handleAddNewLineItemBelow,
    handleRemoveLineItem,
    handleDuplicateLineItem,
    handleUpdateLineItem,
    handleItemFocus,
  } = useLineItemManagement({
    invoiceId: invoice.id,
    invoiceNumber,
    onUpdate,
    initialItems: invoice.items || [],
  });

  const totalQuantity = useMemo(() => {
    return lineItems.reduce((sum, item) => sum + (item.totalQuantity || 0), 0);
  }, [lineItems]);

  const handleDelete = (e) => {
    e.stopPropagation(); // Prevent accordion from toggling
    onDelete(invoice.id);
  };

  const handleUndoClick = (e) => {
    e.stopPropagation(); // Prevent accordion from toggling
    dispatch(
      removeInvoiceItems({
        invoiceNumber: invoice.id.toString(),
      })
    );
  };

  const handleInvoiceNumberChange = (e) => {
    const newValue = e.target.value;
    setInvoiceNumber(newValue);

    // Clear previous timeout
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

    // Set new timeout
    debounceTimeout.current = setTimeout(() => {
      if (onUpdate) {
        onUpdate(invoice.id, {
          invoiceNumber: newValue,
          items: lineItems,
        });
      }
    }, 500); // 500ms delay
  };

  const handleTextFieldFocus = (e) => {
    e.stopPropagation();
    setIsTextFieldFocused(true);
  };

  const handleTextFieldBlur = () => {
    setIsTextFieldFocused(false);
  };

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  return (
    <Box
      sx={{
        border: "1px solid lightgray",
        borderLeft: "1px solid rgb(2, 129, 192)",
      }}
    >
      <Accordion
        defaultExpanded={true}
        sx={{
          mt: 0,
          borderRadius: 0,
          boxShadow: "none",
          borderTop: "1px solid rgba(0, 0, 0, 0.12)",
          "&::before": {
            display: "none",
          },
          "& .MuiPaper-root": {
            borderRadius: 0,
            boxShadow: "none",
          },
          "&:last-of-type": {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
          "&:first-of-type": {
            borderTop: "none",
          },
          "&.Mui-expanded": {
            margin: "0",
          },
          "& .MuiAccordionSummary-root": {
            backgroundColor: "transparent",
            "&:hover": {
              backgroundColor: "#F5F5F5 !important",
            },
            "&:focus-within:not(:hover)": {
              backgroundColor: "transparent",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            backgroundColor: "rgb(245, 247, 250)",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="fast-entry-content"
            id="fast-entry-header"
            sx={{
              position: "sticky",
              top: "0",
              zIndex: "5",
              flex: 1,
              paddingLeft: "8px",
              borderRadius: "0px",
              paddingRight: "8px",
              "& .MuiAccordionSummary-content": {
                margin: "0",
                flex: 1,
                display: "flex",
                alignItems: "center",
              },
              width: "90%",
              height: "48px",
              "&.Mui-expanded": {
                minHeight: "48px",
                height: "48px",
              },
              backgroundColor: "rgb(245, 247, 250) !important",
              "&:hover": {
                backgroundColor: "#F5F5F5 !important",
              },
              "&:focus-within:not(:hover)": {
                backgroundColor: "rgb(245, 247, 250) !important",
              },
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 28% 16% 20% 16% 16%",
                alignItems: "center",
                width: "100%",
                gap: 2,
                paddingRight: "8px",
              }}
            >
              <InvoiceSelectionTooltip>
                <Checkbox
                  checked={selected}
                  onChange={() => onToggleSelected(invoice.id)}
                  onClick={e => e.stopPropagation()}
                  sx={{
                    padding: "4px",
                    marginRight: "0px",
                    marginBottom: "-4px",
                  }}
                  inputProps={{ 'aria-label': 'Select invoice' }}
                />
              </InvoiceSelectionTooltip>
              <TextField
                size="small"
                placeholder="Invoice #"
                value={invoiceNumber}
                onChange={handleInvoiceNumberChange}
                onClick={(e) => e.stopPropagation()}
                onFocus={handleTextFieldFocus}
                onBlur={handleTextFieldBlur}
                autoComplete="off"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <InvoiceNumberTooltip>
                        <InfoIcon
                          sx={{
                            color: "rgba(0, 0, 0, 0.38)",
                            fontSize: "1rem",
                            marginTop: "6px",
                            marginBottom: "3px",
                            cursor: "help",
                          }}
                        />
                      </InvoiceNumberTooltip>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: "100%",
                  borderRadius: "2px",
                  "& .MuiOutlinedInput-input": {
                    paddingTop: "6px",
                    paddingBottom: "3px",
                    height: "24px",
                    position: "relative",
                    zIndex: 1,
                  },
                  "& .MuiOutlinedInput-root": {
                    height: "36px",
                    "& fieldset": {
                      backgroundColor: "white",
                    },
                    "& .MuiInputAdornment-root": {
                      position: "relative",
                      zIndex: 1,
                    },
                  },
                }}
              />

              {/* Show Undo button if in cart composer mode */}
              {invoiceExistsInComposer ? (
                <ActionButton
                  icon={<UndoIcon />}
                  label="Undo Add"
                  onClick={handleUndoClick}
                  hoverColor="rgba(0, 110, 165, 1)"
                />
              ) : (
                <Box />
              )}

              <Typography
                variant="body1"
                sx={{ fontWeight: 500, color: "#373737", textAlign: "center" }}
              >
                Items: {totalQuantity}
              </Typography>

              {/* Use the ActionButton component for Delete */}
              <ActionButton
                icon={<DeleteIcon />}
                label="Delete"
                onClick={handleDelete}
              />
            </Box>
          </AccordionSummary>
        </Box>

        <AccordionDetails
          sx={{
            boxShadow: "none",
            padding: 0,
            borderTop: "1px dashed rgba(0, 0, 0, 0.12)",
            borderRadius: 0,
            "& .MuiPaper-root": {
              borderRadius: 0,
            },
          }}
        >
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 0,
              "& .MuiPaper-root": {
                borderRadius: 0,
              },
            }}
          >
            <Table>
              <TableBody>
                {lineItems.map((item, index) => (
                  <MemoizedFastEntryInvoiceLineItem 
                    key={item.id} 
                    item={{
                      ...item,
                      invoiceNumber: invoiceNumber
                    }}
                    onRemove={() => handleRemoveLineItem(item.id)}
                    onDuplicate={handleDuplicateLineItem}
                    onUpdate={handleUpdateLineItem}
                    onAddNewBelow={handleAddNewLineItemBelow}
                    isNewItem={item.id === newestItemId}
                    isDuplicatedItem={item.id === duplicatedItemId}
                    isHotKeyCreated={item.id === hotKeyCreatedItemId}
                    onFocus={() => handleItemFocus(item.id)}
                    resetPoppers={resetPoppers}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <NewFastEntryLineItem onAdd={handleAddNewLineItem} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

// Wrap FastEntryInvoice in React.memo for performance
export default React.memo(FastEntryInvoice);
